import type { TranslationKeys } from './lt';

const en: TranslationKeys = {
  // App
  app: {
    name: 'A5Suite',
    loading: 'Loading...',
  },

  // Auth
  auth: {
    username: 'Username',
    password: 'Password',
    signIn: 'Sign in',
    signingIn: 'Signing in...',
    loginFailed: 'Login failed',
    signOut: 'Sign out',
  },

  // Navigation
  nav: {
    dashboard: 'Dashboard',
    sales: 'Sales',
    purchases: 'Purchases',
    bankingCash: 'Banking & Cash',
    journal: 'Journal',
    assets: 'Assets',
    reports: 'Reports',
    taxCompliance: 'Tax & Compliance',
    close: 'Close',
    setup: 'Setup',
    accounts: 'Accounts',
    contacts: 'Contacts',
    catalog: 'Catalog',
    aiAudit: 'AI & Audit',
    arena: 'Agent Arena',
    settings: 'Settings',
    inventory: 'Inventory',
    payroll: 'Payroll',
    advanceReports: 'Advance Reports',
  },

  // Common
  common: {
    save: 'Save',
    saving: 'Saving...',
    create: 'Create',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    close: 'Close',
    back: 'Back',
    search: 'Search',
    searchPlaceholder: 'Search by content, doc #...',
    from: 'From',
    to: 'To',
    date: 'Date',
    name: 'Name',
    code: 'Code',
    type: 'Type',
    status: 'Status',
    actions: 'Actions',
    loading: 'Loading...',
    noData: 'No data',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    active: 'Active',
    obsolete: 'Obsolete',
    manage: 'Manage',
    viewAll: 'View all',
    details: 'Details',
    dateFrom: 'Valid From',
    dateTo: 'Valid To',
    person: 'Person',
    content: 'Content',
    amount: 'Amount',
    currency: 'Currency',
    total: 'Total',
    description: 'Description',
    none: '-- None --',
    noPerson: 'No person',
    account: 'Account',
    accountName: 'Account Name',
    docNumber: 'Doc #',
    docType: 'Document Type',
    tryAgain: 'Try Again',
    done: 'Done',
    preview: 'Preview Import',
    required: '* Required',
    language: 'Language',
  },

  // Layout
  layout: {
    noWorkspace: 'No Workspace Active',
    createOrImport: 'Create or import a workspace to get started with your accounting data.',
    manageWorkspaces: 'Manage Workspaces',
    workspaces: 'Workspaces',
    company: 'Company',
    area: 'Area',
    aiAssistant: 'AI Assistant',
    searchCtrlK: 'Search (Ctrl+K)',
    moduleLoading: 'Loading module...',
  },

  // Workspace
  workspace: {
    title: 'Workspaces',
    createNew: 'Create New',
    importLegacy: 'Import Legacy Database',
    noWorkspaces: 'No workspaces',
  },

  // Dashboard
  dashboard: {
    code: 'Code',
    vat: 'VAT',
    invoice: '+ Invoice',
    journalBtn: 'Journal',
    closeReadiness: 'Close Readiness',
    cashPosition: 'Cash Position',
    anomaliesRisk: 'Anomalies & Risk',
    aiActivity: 'AI Activity',
    banking: 'Banking',
    critical: 'critical',
    warnings: 'warnings',
    info: 'info',
    allClear: 'All clear',
    noAnomalies: 'No anomalies detected',
    totalDecisions: 'total decisions logged',
    recentJournal: 'Recent Journal Entries',
    recentInvoices: 'Recent Invoices',
    trialBalance: 'Trial Balance',
    asOfToday: 'As of today',
    vatSummary: 'VAT Summary',
    taxReports: 'Tax reports',
    saftExport: 'SAFT Export',
    xmlForVmi: 'XML for VMI',
    settingsBtn: 'Settings',
    companyAndAccounts: 'Company & accounts',
    accounts: 'Accounts',
    trialBalanceItems: 'Trial Balance Items',
    invoicesMade: 'Invoices (Made)',
    debit: 'Debit',
    credit: 'Credit',
    class: 'Class',
    longTermAssets: 'Long-term assets',
    shortTermAssets: 'Short-term assets',
    equity: 'Equity',
    liabilities: 'Liabilities',
    sales: 'Sales',
    costs: 'Costs',
    noEntries: 'No entries',
    noInvoices: 'No invoices',
    ar: 'AR',
    ap: 'AP',
    out: 'OUT',
    in: 'IN',
    decisionLog: 'Decision Log',
  },

  // Invoices
  invoices: {
    salesTitle: 'Sales Invoices (Made)',
    purchaseTitle: 'Purchase Invoices (Received)',
    newInvoice: '+ New Invoice',
    salesTab: 'Sales (Made)',
    purchasesTab: 'Purchases (Received)',
    draft: 'Draft',
    post: 'Post',
    client: 'Client',
    supplier: 'Supplier',
    sum: 'Sum',
    vat: 'VAT',
    deleteTitle: 'Delete Invoice',
    deleteMessage: 'Are you sure you want to delete invoice #{{id}}? This action cannot be undone.',
    postTitle: 'Post Invoice',
    postMessage: 'Post invoice #{{id}} to the general ledger? This will create a journal entry.',
    noInvoicesFound: 'No invoices found',
    // Credit Note
    creditNoteTitle: 'New Credit Note',
    createCreditNote: 'Credit Note',
    creatingCreditNote: 'Creating credit note...',
    linkedInvoice: 'Linked Invoice',
    unlinkInvoice: 'Unlink',
    searchInvoicePlaceholder: 'Search invoice by number, person...',
    creditNoteFor: 'Credit note for',
    creditNoteContentPlaceholder: 'Credit note description...',
    invoiceDate: 'Invoice Date',
    documentNumber: 'Document #',
    currencyRate: 'Currency Rate',
    unit: 'Unit',
    price: 'Price',
    qty: 'Qty',
    vatRate: 'VAT Rate',
    lineItems: 'Line Items',
    addLine: 'Add Line',
    none: 'None',
    totalWithVat: 'Total with VAT',
    selectPerson: '-- Select --',
  },

  // Journal
  journal: {
    title: 'Journal Entries',
    newEntry: '+ New Entry',
    allEntries: 'All Entries',
    postingLog: 'Posting Log',
    templates: 'Templates',
    source: 'Source',
    allSources: 'All Sources',
    manualEntry: 'Manual Entry',
    invoicePosting: 'Invoice Posting',
    bankOperation: 'Bank Operation',
    periodClosing: 'Period Closing',
    aiGenerated: 'AI Generated',
    adjustment: 'Adjustment',
    debit: 'Debit',
    credit: 'Credit',
    balanced: 'Balanced',
    notBalanced: 'Not balanced',
    unbalanced: 'UNBALANCED',
    totalDebit: 'Total Debit',
    totalCredit: 'Total Credit',
    difference: 'Difference',
    noJournalEntriesFound: 'No journal entries found',
    noPostingLogEntries: 'No posting log entries',
    posted: 'Posted',
    postedDate: 'Posted Date',
    auditTrailDescription: 'Audit trail of all journal postings showing who, what, when, and source. Every financial posting is immutable once created.',
    createTemplate: '+ Create Template',
    useTemplate: 'Use Template',
    // Journal Edit
    editEntry: 'Edit Journal Entry #{{id}}',
    newJournalEntry: 'New Journal Entry',
    documentNumber: 'Document #',
    documentType: 'Document Type',
    content: 'Content',
    person: 'Person',
    contentPlaceholder: 'Description of the journal entry...',
    docRequired: 'Document number is required',
    contentRequired: 'Content is required',
    lineWithAccountRequired: 'At least one line with an account and positive amount is required',
    everyLineMustHaveAccount: 'Every line with an amount must have an account selected',
    entryNotBalanced: 'Entry is not balanced. Debit: {{debit}}, Credit: {{credit}}, Difference: {{difference}}',
    futureDateWarning: 'Warning: the date is in the future.',
    noDebitLines: 'No debit lines',
    noCreditLines: 'No credit lines',
    debitTotal: 'Debit (Debetas)',
    creditTotal: 'Credit (Kreditas)',
    addDebit: '+ Debit',
    addCredit: '+ Credit',
    saveChanges: 'Save Changes',
    createEntry: 'Create Entry',
    // Journal Detail
    entryNotFound: 'Entry not found',
    entryDeleted: 'Journal entry #{{id}} deleted',
    deleteTitle: 'Delete Journal Entry',
    deleteMessage: 'Permanently delete journal entry #{{id}}? This cannot be undone.',
    cloneTitle: 'Clone Journal Entry',
    cloneMessage: 'Create a copy of journal entry #{{id}} with today\'s date?',
    clone: 'Clone',
    lines: 'Lines',
    // Document types
    docTypeGeneric: 'Generic',
    docTypeSalesInvoice: 'Sales Invoice',
    docTypePurchaseInvoice: 'Purchase Invoice',
    docTypeBankOp: 'Bank Operation',
    docTypeCashIncome: 'Cash Income',
    docTypeCashSpending: 'Cash Spending',
    docTypeOffset: 'Offset',
    docTypeWageSheet: 'Wage Sheet',
  },

  // Settings
  settings: {
    title: 'Settings',
    companyInfo: 'Company Information',
    companyName: 'Name',
    companyCode: 'Company Code',
    vatCode: 'VAT Code',
    address: 'Address',
    headPerson: 'Head Person',
    createCompany: 'Create Company',
    createCompanyDescription: 'No company configured yet. Fill in the details below to get started.',
    taxRates: 'Tax Rates',
    addTaxRate: '+ Add Tax Rate',
    rate: 'Rate (%)',
    vatDebitAccount: 'VAT Debit Account',
    vatCreditAccount: 'VAT Credit Account',
    vatPurchaseAccount: 'VAT Purchase Account',
    validFrom: 'Valid From',
    noTaxRates: 'No tax rates',
    failedToCreateTaxRate: 'Failed to create tax rate',
    documentSerials: 'Document Serials',
    manageSerials: 'Manage Serials',
    lastNumber: 'Last #',
    pattern: 'Pattern',
    reset: 'Reset',
    noDocumentSerials: 'No document serials configured',
    importLegacyDb: 'Import Legacy Database',
    importDescription: 'Upload an Apskaita5 .db file to create a new workspace and import data.',
    previewImport: 'Preview Import',
    alreadyImported: 'This database was already imported as workspace "{{name}}". Re-importing will create a separate workspace with the same data.',
    entity: 'Entity',
    records: 'Records',
    importIntoNewWorkspace: 'Import into New Workspace',
    importSuccessful: 'Import successful! {{count}} records imported into workspace "{{name}}".',
    table: 'Table',
    aiAgentSettings: 'AI Agent Settings',
    aiAgentDescription: 'Configure authority levels per AI workflow',
    workflow: 'Workflow',
    authorityLevel: 'Authority Level',
    aiDescription: 'Description',
    noAiWorkflows: 'No AI workflows configured',
    authorityAuditNote: 'Authority changes are audit-logged. Default is "Suggest" until explicitly promoted.',
    saveChanges: 'Save Changes ({{count}})',
    administration: 'Administration',
    docSerialsDescription: 'Manage document numbering patterns, prefixes, and sequences',
    userRolesPermissions: 'User Roles & Permissions',
    rolesDescription: 'Manage roles and module-level access control',
    dataBackup: 'Data Backup',
    backupDescription: 'Export company data as JSON backup file',
    emailConfiguration: 'Email Configuration',
    emailDescription: 'SMTP settings for invoice email sending',
  },

  // Accounts
  accounts: {
    title: 'Chart of Accounts',
    trialBalance: 'Trial Balance',
    newAccount: '+ New Account',
    accountNumber: 'Account Number',
    saftCode: 'SAFT Code',
    number: 'Number',
    noAccounts: 'No accounts found',
    deleteConfirm: 'Delete account {{number}}?',
    classLabel: 'Class',
  },

  // Persons
  persons: {
    title: 'Contacts',
    subtitle: 'Unified customers, vendors, and employees',
    addContact: 'Add Contact',
    total: 'Total',
    customers: 'Customers',
    vendors: 'Vendors',
    employees: 'Employees',
    custVend: 'Cust+Vend',
    searchPlaceholder: 'Search by name, code, or VAT code...',
    allContacts: 'All Contacts',
    customerOnly: 'Customers Only',
    vendorOnly: 'Vendors Only',
    employeeOnly: 'Employees Only',
    custPlusVend: 'Customer + Vendor',
    hasAnyRole: 'Has any role',
    receivables: 'Receivables',
    payables: 'Payables',
    roles: 'Roles',
    noRole: 'No role',
    both: 'Both',
    noContacts: 'No contacts found',
    editContact: 'Edit Contact',
    newContact: 'New Contact',
    receivablesAccount: 'Receivables Account',
    payablesAccount: 'Payables Account',
    customer: 'Customer',
    vendor: 'Vendor',
    employee: 'Employee',
    naturalPerson: 'Natural Person',
    update: 'Update',
    personDeleted: 'Person #{{id}} deleted',
    deleteConfirm: 'Delete {{name}}?',
    inactive: 'Inactive',
    // Address fields
    street: 'Street',
    city: 'City',
    postalCode: 'Postal Code',
    country: 'Country',
    // Contact fields
    contactName: 'Contact Person',
    contactPhone: 'Phone',
    contactEmail: 'Email',
    // Business terms
    paymentTermsDays: 'Payment Terms (days)',
    discountPercent: 'Discount (%)',
    creditLimit: 'Credit Limit',
    sector: 'Business Sector',
    notes: 'Notes',
  },

  // Periods
  periods: {
    closeTitle: 'Period Close',
  },

  // Reports
  reports: {
    vatSummary: 'VAT Summary',
    vatDeclaration: 'VAT Declaration',
    financialStatements: 'Financial Statements',
    debtAging: 'Debt Aging',
    exportSaft: 'SAFT Export',
  },

  // Banking
  banking: {
    title: 'Banking & Cash',
    import: 'Bank Statement Import',
    importStatement: 'Import Statement',
    newOperation: '+ New Operation',
    cashOperations: 'Cash Operations',
    offsets: 'Offsets',
    inflowCredit: 'Inflow (Credit)',
    outflowDebit: 'Outflow (Debit)',
    netCashMovement: 'Net Cash Movement',
    debitAcct: 'Debit Acct',
    creditAcct: 'Credit Acct',
    noBankOperations: 'No bank operations found for this period',
    editBankOperation: 'Edit Bank Operation',
    newBankOperation: 'New Bank Operation',
    documentNumberLabel: 'Document Number *',
    amountLabel: 'Amount *',
    debitAccountLabel: 'Debit Account',
    creditAccountLabel: 'Credit Account',
    bankAccountCodeLabel: 'Bank Account Code',
    personLabel: 'Person',
    currencyLabel: 'Currency',
    currencyRateLabel: 'Currency Rate',
    updateOperation: 'Update',
    createOperation: 'Create',
    deleteOperation: 'Delete operation',
    operationDeleted: 'Bank operation #{{id}} deleted',
    reconciliationWorkspace: 'Reconciliation Workspace',
    reconciliationDescription: 'Bank reconciliation will match bank feed transactions with internal records. This feature requires bank feed integration to be configured in Settings.',
    startReconciliation: 'Start Reconciliation (coming soon)',
    selectAccount: '-- Select --',
    selectPerson: '-- None --',
    docNumberPlaceholder: 'e.g. BO-001',
    // Bank Import
    smartBankImport: 'Smart Bank Import',
    backToBanking: 'Back to Banking',
    total: 'Total',
    highConfidence: 'High Confidence',
    partialMatch: 'Partial Match',
    noMatch: 'No Match',
    accepted: 'Accepted',
    totalInflow: 'Total Inflow',
    totalOutflow: 'Total Outflow',
    detectedFormat: 'Detected Format',
    acceptAllGreen: 'Accept all green ({{count}})',
    acceptAll: 'Accept all',
    confirmOperations: 'Confirm {{count}} operations',
    creating: 'Creating...',
    parseWarnings: 'Parse Warnings',
    counterparty: 'Counterparty',
    confidence: 'Confidence',
    importComplete: 'Import Complete',
    created: 'Created',
    skipped: 'Skipped',
    errors: 'Errors',
    importAnotherFile: 'Import Another File',
    viewBankingOperations: 'View Banking Operations',
    accountNumberHint: 'Account number for your bank account in the chart of accounts',
    dragAndDrop: 'Drag & drop a bank statement file here',
    supportedFormats: 'Supported formats: CSV (SEB, Swedbank, Luminor), XML (Camt.053), MT940 (SWIFT)',
    browseFiles: 'Browse Files',
    pasteCsvText: 'Paste CSV Text',
    pasteBankStatement: 'Paste Bank Statement',
    backToFileUpload: 'Back to file upload',
    bankFormat: 'Bank Format',
    autoDetect: 'Auto-detect',
    parseAndMatch: 'Parse & Match',
    parsing: 'Parsing...',
    noOperations: 'No operations parsed',
    // Bank Accounts
    bankAccounts: 'Bank Accounts',
    manageAccounts: 'Manage Accounts',
    newAccount: '+ New Account',
    accountDeleted: 'Bank account deleted',
    accountUpdated: 'Bank account updated',
    accountCreated: 'Bank account created',
    deleteAccountConfirm: 'Delete this bank account?',
    accounts: 'Accounts',
    balances: 'Balances',
    statement: 'Statement',
    accountName: 'Account Name',
    bankNameCol: 'Bank',
    glAccount: 'GL Account',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    type: 'Type',
    noBankAccounts: 'No bank accounts configured',
    totalNet: 'Net Balance',
    debit: 'Debit',
    credit: 'Credit',
    netBalance: 'Net Balance',
    noBalances: 'No balances available',
    openingBalance: 'Opening',
    periodDebit: 'Period Debit',
    periodCredit: 'Period Credit',
    periodNet: 'Period Net',
    closingBalance: 'Closing',
    noTransactions: 'No transactions in this period',
    selectAccountForStatement: 'Select a bank account to view its statement',
    chooseAccount: '-- Choose Account --',
    allTypes: 'All',
    typeBank: 'Bank Account',
    typePseudo: 'Pseudo Bank',
    typeTill: 'Cash Register',
    accountNameLabel: 'Account Name',
    bankNameLabel: 'Bank Name',
    bankCodeLabel: 'Bank Code',
    typeLabel: 'Account Type',
    glAccountLabel: 'GL Account Number',
    editAccount: 'Edit Bank Account',
    // Reconciliation
    showUnreconciled: 'Show Unreconciled',
    hideUnreconciled: 'Hide Unreconciled',
    unreconciledCount: 'unreconciled transaction(s)',
    markReconciled: 'Mark Reconciled',
    allReconciled: 'All transactions are reconciled!',
    reconciliationUpdated: 'Reconciliation status updated',
  },

  // AI Audit
  aiAudit: {
    title: 'AI & Audit',
  },

  // Agent Arena
  arena: {
    title: 'Agent Arena',
    description: 'AI agent testing and competition environment.',
    comingSoon: 'Arena feature is being prepared. Stay tuned for updates.',
  },

  // Audit Trail
  audit: {
    title: 'Audit Trail',
  },

  // Catalog
  catalog: {
    title: 'Goods & Services Catalog',
  },

  // Error Boundary
  errorBoundary: {
    title: 'Something went wrong',
    message: 'An error occurred in the application. Try reloading the page.',
    reload: 'Reload',
  },

  // Confirm Dialog
  confirmDialog: {
    cancel: 'Cancel',
  },

  // Modules
  modules: {
    advanceReports: 'Advance Reports',
    fixedAssets: 'Fixed Assets',
    inventory: 'Inventory',
    payroll: 'Payroll',
  },
};

export default en;
